import { Calculator } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { RateAverageView } from '@obraplan/core/application/use-cases/takeoff/rate-average-use-cases';
import type { ProjectId } from '@obraplan/core/domain/shared/ids';
import {
  APARTMENT_OUT_LABEL,
  type ApartmentAverage,
  type ApartmentOutOfAverage,
  type ApartmentRoomKindAverage,
  type ApartmentSteelAverage,
} from '@obraplan/core/domain/takeoff/apartment-average';
import { APPLIES_TO_LABEL } from '@obraplan/core/domain/takeoff/applies-to';
import {
  FOUNDATION_PIECE_OUT_LABEL,
  type FoundationAverages,
  type FoundationPieceOutReason,
  type FoundationPiecesOut,
} from '@obraplan/core/domain/takeoff/foundation-average';
import type { PieceElement } from '@obraplan/core/domain/takeoff/foundation-factors';
import type { StructureAverages } from '@obraplan/core/domain/takeoff/structure-average';
import { steelGaugeLabel, type SteelGauge } from '@obraplan/core/domain/takeoff/steel';
import { ROOM_KIND_LABEL } from '@obraplan/core/domain/takeoff/room';
import {
  diameterLabel,
  OUT_OF_AVERAGE_LABEL,
  type OutOfAverage,
  type OutOfAverageReason,
  type RateAverage,
  type RateMeasureUnit,
} from '@obraplan/core/domain/takeoff/rate-average';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableWrapper,
} from '@/components/ui/table';
import { TabList, TabPanel } from '@/components/ui/tabs';
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/states';
import { useContainer, useSessionState } from '@/features/session/use-session';
import { useCommand } from '@/lib/use-command';
import { useLive } from '@/lib/use-live';
import { metros, primeiraMaiuscula } from './formato';

/**
 * Configurações → Serviços → Taxas médias (Fase 39, etapa 1 — RN-503).
 *
 * ```text
 * Obras        Veneza ● referência  ☑ na média   aprovada · 7 serviços   108 estacas
 *              Montrezor            ☑ na média   provisória              121 estacas
 * R40 · R60 · R80
 *   CONCRETAGEM      CONCRETO FCK 35         1,0500 M³/m³ de estaca   2 obras · 66 estacas
 * ```
 *
 * É a vitrine da média, e só: nada aqui vira índice de obra nenhuma (isso é a etapa 2, no tipo
 * de estaca). O que a aba precisa deixar claro é **de onde** cada número veio — quantas obras,
 * quantas estacas — e o que ficou de fora e por quê, porque é isso que o orçamentista confere
 * antes de confiar na média.
 */
export function TaxasMedias() {
  const container = useContainer();
  const { session } = useSessionState();
  const lida = useLive(
    () =>
      session === undefined
        ? Promise.resolve(undefined)
        : container.getRateAverages.execute(session),
    [session?.userId],
  );

  const [marcar, marcando] = useCommand(
    (projectId: ProjectId, entersAverage: boolean) =>
      container.setProjectInRateAverage.execute({ projectId, entersAverage }, session!),
    { successMessage: 'Obras da média atualizadas.' },
  );
  const [referenciar, referenciando] = useCommand(
    (projectId: ProjectId) => container.setCatalogReferenceProject.execute({ projectId }, session!),
    { successMessage: 'Obra de referência escolhida.' },
  );

  const visao = lida !== undefined && lida.ok ? lida.value : undefined;
  const diametros = useMemo(() => visao?.diameters ?? [], [visao]);
  const [escolhido, setEscolhido] = useState<string | undefined>(undefined);
  const atual =
    diametros.find((d) => String(d.diameter) === escolhido) ?? diametros[0] ?? undefined;

  if (lida === undefined) return <LoadingState label="Carregando as taxas médias…" />;
  if (!lida.ok) {
    return (
      <ErrorState
        title="Não foi possível ler as taxas médias"
        message={lida.error.message}
        code={lida.error.code}
      />
    );
  }
  const {
    projects,
    reference,
    averages,
    outOfAverage,
    canManage,
    apartment,
    foundation,
    structure,
  } = lida.value;
  const ocupado = marcando.isPending || referenciando.isPending;
  const nomeDaObra = new Map(projects.map((project) => [project.id, project.name]));
  const marcadas = projects.filter((project) => project.entersAverage);

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle>Obras da média</CardTitle>
          <CardDescription>
            A taxa sai do que as obras marcadas gastaram por unidade da medida da planta. Só entra o
            item que a obra de referência também tem.
            {!canManage && ' Só quem administra o cadastro global muda as marcas.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <TableWrapper>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Obra</TableHead>
                  <TableHead>Orçamento das estacas</TableHead>
                  <TableHead className="hidden text-right md:table-cell">Estacas</TableHead>
                  <TableHead className="hidden md:table-cell">Apartamento</TableHead>
                  <TableHead>Na média</TableHead>
                  <TableHead>Referência</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {projects.map((project) => (
                  <LinhaDaObra
                    key={project.id}
                    project={project}
                    canManage={canManage}
                    ocupado={ocupado}
                    onMarcar={(valor) => void marcar(project.id, valor)}
                    onReferenciar={() => void referenciar(project.id)}
                  />
                ))}
              </TableBody>
            </Table>
          </TableWrapper>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Taxas por diâmetro</CardTitle>
          <CardDescription>
            Média ponderada: tudo o que as estacas gastaram dividido por toda a medida delas. O
            diâmetro sai do fim do nome (E06-A - R40), o comprimento da perfuração da peça, e o aço
            é por estaca. A perda já está dentro da taxa.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {reference === undefined ? (
            <EmptyState
              title="Escolha a obra de referência"
              description="Sem ela não há média: é o cadastro dela que diz quais itens cada serviço da estaca tem."
              icon={<Calculator className="size-8" />}
            />
          ) : marcadas.length === 0 ? (
            <EmptyState
              title="Marque as obras que entram na média"
              description="Aprovadas e provisórias entram do mesmo jeito; a versão de orçamento nunca entra."
              icon={<Calculator className="size-8" />}
            />
          ) : atual === undefined ? (
            <EmptyState
              title="Nenhuma estaca deu média"
              description="As obras marcadas não têm estaca da planilha com o tipo no fim do nome e a perfuração na peça. Veja o que ficou de fora abaixo."
              icon={<Calculator className="size-8" />}
            />
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <TabList
                  base="taxas-por-diametro"
                  items={diametros.map((d) => ({
                    id: String(d.diameter),
                    label: diameterLabel(d.diameter),
                  }))}
                  atual={String(atual.diameter)}
                  onEscolher={setEscolhido}
                  label="Diâmetros de estaca"
                />
                <p className="tabular text-sm text-(--color-content-muted)">
                  Ø {metros(atual.diameter * 100, 0)} cm · {quantas(atual.pieces, 'estaca')} de{' '}
                  {quantas(atual.projects, 'obra')}
                </p>
              </div>
              <TabPanel base="taxas-por-diametro" id={String(atual.diameter)}>
                <TabelaDoDiametro
                  averages={averages.filter((a) => a.diameter === atual.diameter)}
                />
              </TabPanel>
            </>
          )}
        </CardContent>
      </Card>

      {reference !== undefined && marcadas.length > 0 && (
        <TaxasDoApartamento
          averages={apartment.averages}
          porTipo={apartment.byRoomKind}
          aco={apartment.steel}
          volumeDeLaje={apartment.slabVolume}
          fora={apartment.outOfAverage}
          nomeDaObra={nomeDaObra}
        />
      )}

      {/* A média da fundação (Fase 41, etapa 3 — RN-555). Ausente: o servidor de antes dela. */}
      {reference !== undefined && marcadas.length > 0 && foundation !== undefined && (
        <TaxasDaFundacao media={foundation} nomeDaObra={nomeDaObra} />
      )}

      {/* A média da estrutura (Fase 42, etapa 4 — RN-585). Ausente: o servidor de antes dela. */}
      {reference !== undefined && marcadas.length > 0 && structure !== undefined && (
        <TaxasDaEstrutura media={structure} nomeDaObra={nomeDaObra} />
      )}

      {outOfAverage.length > 0 && <ForaDaMedia itens={outOfAverage} nomeDaObra={nomeDaObra} />}
    </div>
  );
}

function LinhaDaObra({
  project,
  canManage,
  ocupado,
  onMarcar,
  onReferenciar,
}: {
  readonly project: RateAverageView['projects'][number];
  readonly canManage: boolean;
  readonly ocupado: boolean;
  readonly onMarcar: (valor: boolean) => void;
  readonly onReferenciar: () => void;
}) {
  return (
    <TableRow>
      <TableCell>
        <span className="font-medium">{project.name}</span>
        <span className="ml-2 font-mono text-xs text-(--color-content-muted)">{project.code}</span>
      </TableCell>
      <TableCell>
        {project.approvedPileServices > 0 ? (
          <Badge tone="ok" className="whitespace-nowrap">
            aprovado · {quantas(project.approvedPileServices, 'serviço', 'serviços')}
          </Badge>
        ) : project.importedPiles + project.drawnPiles > 0 ? (
          <Badge tone="neutral">provisório</Badge>
        ) : (
          <span className="text-sm text-(--color-content-muted)">sem estacas</span>
        )}
      </TableCell>
      <TableCell className="tabular hidden text-right md:table-cell">
        {project.importedPiles}
        {project.drawnPiles > 0 && (
          <span className="block text-xs text-(--color-content-muted)">
            + {quantas(project.drawnPiles, 'desenhada')}
          </span>
        )}
      </TableCell>
      <TableCell className="hidden text-sm md:table-cell">
        {project.measuredUnitTypes > 0 ? (
          <span className="tabular text-(--color-content-muted)">
            {quantas(project.measuredUnitTypes, 'tipologia medida', 'tipologias medidas')}
          </span>
        ) : project.entersAverage ? (
          // RN-523: a obra marcada sem a foto da medida não dá média do apartamento.
          <span className="text-(--color-state-warn)">sem medida: abra o quantitativo</span>
        ) : (
          <span className="text-(--color-content-muted)">—</span>
        )}
      </TableCell>
      <TableCell>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="size-4"
            checked={project.entersAverage}
            disabled={!canManage || ocupado}
            aria-label={`${project.name} entra na média`}
            onChange={(event) => onMarcar(event.target.checked)}
          />
          <span className="hidden md:inline">{project.entersAverage ? 'entra' : 'fora'}</span>
        </label>
      </TableCell>
      <TableCell>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="radio"
            name="obra-de-referencia"
            className="size-4"
            checked={project.isReference}
            disabled={!canManage || ocupado}
            aria-label={`${project.name} é a obra de referência`}
            onChange={onReferenciar}
          />
          {project.isReference ? <Badge tone="info">referência</Badge> : null}
        </label>
      </TableCell>
    </TableRow>
  );
}

/**
 * As taxas do apartamento (Fase 40, RN-524): por disciplina › serviço, o item, a taxa sobre a
 * unidade da medida da planta e de onde veio. Como a da estaca, o serviço é linha de título.
 */
function TaxasDoApartamento({
  averages,
  porTipo,
  aco,
  volumeDeLaje,
  fora,
  nomeDaObra,
}: {
  readonly averages: readonly ApartmentAverage[];
  /** A média por tipo de cômodo nos serviços de ambiente (Fase 40, etapa 2 — RN-525). */
  readonly porTipo: readonly ApartmentRoomKindAverage[];
  /** A média da taxa de aço da laje, por bitola (etapa 3 — RN-532). */
  readonly aco: readonly ApartmentSteelAverage[];
  /** O volume de laje das obras marcadas: zero é "sem laje desenhada" (RN-535). */
  readonly volumeDeLaje: number;
  readonly fora: readonly ApartmentOutOfAverage[];
  readonly nomeDaObra: ReadonlyMap<ProjectId, string>;
}) {
  const origem = (average: ApartmentAverage): string =>
    `${quantas(average.projects, 'obra')} · ${quantas(average.unitTypes, 'tipologia')}`;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Taxas do apartamento</CardTitle>
        <CardDescription>
          Parede, piso, teto, rodapé, os vãos, a laje e o que é por unidade. Cada obra marcada entra
          com o índice dela vezes a medida da planta, e cada tipologia pesa pelas vezes que se
          repete na obra. O índice que veio da própria média não entra. A perda já está dentro da
          taxa.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {averages.length === 0 ? (
          <EmptyState
            title="Nenhum serviço do apartamento deu média"
            description="As obras marcadas precisam ter índice nos itens do apartamento e o quantitativo aberto depois da Fase 40, para a medida da planta ser fotografada."
            icon={<Calculator className="size-8" />}
          />
        ) : (
          <TabelaDeMedias averages={averages} origem={origem} porTipo={porTipo} />
        )}
        <section aria-label="Aço da laje" className="flex flex-col gap-1">
          <h3 className="text-sm font-medium">Aço da laje (kg/m³)</h3>
          {volumeDeLaje <= 0 ? (
            <p className="text-sm text-(--color-content-muted)">
              Nenhuma laje desenhada nas obras marcadas: sem média de laje nem de aço.
            </p>
          ) : aco.length === 0 ? (
            <p className="text-sm text-(--color-content-muted)">
              Há laje desenhada ({metros(volumeDeLaje, 2)} m³), mas nenhuma taxa de aço digitada na
              disciplina dela.
            </p>
          ) : (
            <ul className="flex flex-col gap-0.5 text-sm">
              {aco.map((average) => (
                <li key={average.gauge} className="tabular flex justify-between gap-3">
                  <span>{steelGaugeLabel(average.gauge)}</span>
                  <span>
                    {metros(average.rate, 3)}{' '}
                    <span className="text-(--color-content-muted)">
                      · {quantas(average.projects, 'obra')}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
        {fora.length > 0 && (
          <section className="flex flex-col gap-1">
            <h3 className="text-sm font-medium">
              Fora da média: {APARTMENT_OUT_LABEL.ITEM_SEM_PAR}
            </h3>
            <ul className="flex flex-col gap-0.5 text-sm text-(--color-content-muted)">
              {fora.map((item) => (
                <li key={`${item.projectId}|${item.serviceName}|${item.description}`}>
                  {nomeDaObra.get(item.projectId) ?? 'Obra'} · {item.serviceName} ·{' '}
                  {item.description}
                  <span className="tabular"> ({quantas(item.unitTypes, 'tipologia')})</span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * A tabela das médias por serviço × item — a do apartamento e a da fundação. O serviço é linha de
 * título, como na da estaca; a origem desce para debaixo do item no celular.
 */
function TabelaDeMedias({
  averages,
  origem,
  porTipo = [],
}: {
  readonly averages: readonly ApartmentAverage[];
  /** "2 obras · 3 tipologias", "2 obras · 14 peças". */
  readonly origem: (average: ApartmentAverage) => string;
  /** A média por tipo de cômodo, só no apartamento (Fase 40, etapa 2). */
  readonly porTipo?: readonly ApartmentRoomKindAverage[];
}) {
  return (
    <TableWrapper>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Item</TableHead>
            <TableHead className="text-right">Taxa</TableHead>
            <TableHead className="hidden text-right md:table-cell">Origem</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {averages.flatMap((average, indice) => {
            const linhas = [];
            if (averages[indice - 1]?.serviceKey !== average.serviceKey) {
              linhas.push(
                <TableRow key={`servico|${average.serviceKey}`}>
                  <TableCell
                    colSpan={3}
                    className="bg-(--color-surface-muted) py-1.5 text-xs font-semibold uppercase tracking-[0.06em]"
                  >
                    {average.disciplineName} › {average.serviceName}
                  </TableCell>
                </TableRow>,
              );
            }
            const doItem = porTipo.filter(
              (tipo) =>
                tipo.serviceKey === average.serviceKey &&
                tipo.item.description === average.item.description,
            );
            linhas.push(
              <TableRow key={`${average.serviceKey}|${average.item.description}`}>
                <TableCell>
                  {average.item.description}
                  {average.item.code !== undefined && (
                    <span className="ml-2 font-mono text-xs text-(--color-content-muted)">
                      {average.item.code}
                    </span>
                  )}
                  <span className="block text-xs text-(--color-content-muted) md:hidden">
                    {origem(average)}
                  </span>
                  {doItem.length > 0 && (
                    <span className="tabular block text-xs text-(--color-content-muted)">
                      por tipo:{' '}
                      {doItem
                        .map((tipo) => `${ROOM_KIND_LABEL[tipo.roomKind]} ${metros(tipo.rate, 4)}`)
                        .join(' · ')}
                    </span>
                  )}
                </TableCell>
                <TableCell className="tabular whitespace-nowrap text-right">
                  {metros(average.rate, 4)}{' '}
                  <span className="text-(--color-content-muted)">
                    {average.item.kind === 'MAO_DE_OBRA' ? 'HH' : (average.item.unit ?? '')}/
                    {average.measureUnit}
                  </span>
                </TableCell>
                <TableCell className="tabular hidden whitespace-nowrap text-right text-sm text-(--color-content-muted) md:table-cell">
                  {origem(average)}
                </TableCell>
              </TableRow>,
            );
            return linhas;
          })}
        </TableBody>
      </Table>
    </TableWrapper>
  );
}

const ELEMENTOS_DA_FUNDACAO: readonly PieceElement[] = ['BLOCO', 'BALDRAME', 'RADIER'];

/** Os elementos do aço da estrutura (Fase 42, RN-580): o muro e o pilar de contenção separados. */
const ELEMENTOS_DA_ESTRUTURA: readonly PieceElement[] = [
  'PILAR',
  'VIGA',
  'ESCADA',
  'MURO_CONTENCAO',
  'PILAR_CONTENCAO',
];

const MOTIVOS_DA_PECA_FORA: readonly FoundationPieceOutReason[] = [
  'SEM_QUANTIDADE_DO_SERVICO',
  'SEM_CONCRETAGEM',
];

/** A média do aço de um elemento por bitola — a da fundação e a da estrutura têm este molde. */
interface AcoDoElemento {
  readonly element: PieceElement;
  readonly gauge: SteelGauge;
  readonly rate: number;
  readonly projects: number;
  /** Quantas peças usam a bitola. Ausente: o servidor da fundação de antes da revisão. */
  readonly pieces?: number;
}

/** O que o cartão das peças lê: a média da fundação (Fase 41) ou a da estrutura (Fase 42). */
interface MediaDasPecas {
  readonly averages: readonly ApartmentAverage[];
  readonly outOfAverage: readonly ApartmentOutOfAverage[];
  readonly steel: readonly AcoDoElemento[];
  readonly pieces: number;
  readonly piecesOut?: readonly FoundationPiecesOut[];
}

/**
 * As taxas da fundação (Fase 41, etapa 3 — RN-551, RN-552, RN-555): bloco, baldrame e radier, uma
 * média só por serviço × item para todas as peças (F5), o aço por elemento e bitola, e o que
 * ficou fora — o item sem par, e a peça da planilha sem a medida do serviço ou sem a concretagem.
 * No molde das do apartamento, na mesma aba.
 */
function TaxasDaFundacao({
  media,
  nomeDaObra,
}: {
  readonly media: FoundationAverages;
  readonly nomeDaObra: ReadonlyMap<ProjectId, string>;
}) {
  return (
    <TaxasDasPecas
      media={media}
      nomeDaObra={nomeDaObra}
      titulo="Taxas da fundação"
      descricao="Bloco, baldrame e radier, com uma média só por serviço e item para todas as peças. A peça desenhada entra com a medida da planta vezes o fator que vale nela; a da planilha por peça, com a quantidade do item dividida pela do serviço na mesma peça. O índice que veio da própria média não entra. A perda já está dentro da taxa."
      semPecas="As obras marcadas não têm bloco, baldrame nem radier: sem média da fundação."
      semMedia={{
        title: 'Nenhum serviço da fundação deu média',
        description:
          'As peças das obras marcadas precisam de índice nos itens dos serviços que medem o bloco, o baldrame e o radier — ou da quantidade do serviço na planilha por peça.',
      }}
      aco={{
        rotulo: 'Aço da fundação',
        elementos: ELEMENTOS_DA_FUNDACAO,
        vazio:
          'Nenhuma taxa de aço nas peças, nos tipos de bloco nem nas disciplinas da fundação das obras marcadas.',
      }}
    />
  );
}

/**
 * As taxas da estrutura (Fase 42, etapa 4 — RN-576 a RN-580, RN-585): pilar, viga, escada, muro e
 * pilar de contenção desenhados, e o arranque da planilha. Uma média só por serviço × item para
 * todas as peças do elemento (G9); o aço por elemento e bitola, com o muro e o pilar de contenção
 * separados; de quantas obras e peças; e o que ficou fora. O cartão da fundação, com outras peças.
 */
function TaxasDaEstrutura({
  media,
  nomeDaObra,
}: {
  readonly media: StructureAverages;
  readonly nomeDaObra: ReadonlyMap<ProjectId, string>;
}) {
  return (
    <TaxasDasPecas
      media={media}
      nomeDaObra={nomeDaObra}
      titulo="Taxas da estrutura"
      descricao="Pilar, viga, escada, muro e pilar de contenção, e o arranque da planilha por peça, com uma média só por serviço e item para todas as peças do elemento. A peça desenhada entra com a medida da planta vezes o fator que vale nela — o próprio, o do tipo ou o índice da obra —, e o pilar e a viga pesam pelas vezes que o desenho deles se repete na obra. A da planilha, com a quantidade do item dividida pela do serviço na mesma peça: o arranque entra assim nos itens do corte e dobra, por kg. O que veio da própria média não entra. A perda já está dentro da taxa."
      semPecas="As obras marcadas não têm pilar, viga, escada, contenção nem arranque: sem média da estrutura."
      semMedia={{
        title: 'Nenhum serviço da estrutura deu média',
        description:
          'As peças das obras marcadas precisam de índice ou fator nos itens dos serviços que medem o pilar, a viga, a escada e a contenção — ou da quantidade do serviço na planilha por peça.',
      }}
      aco={{
        rotulo: 'Aço da estrutura',
        elementos: ELEMENTOS_DA_ESTRUTURA,
        vazio:
          'Nenhuma taxa de aço nas peças, nos tipos de pilar e de viga nem nas disciplinas da estrutura das obras marcadas.',
        nota: 'O arranque não tem volume: entra nos itens por kg, e fica fora do aço por m³.',
      }}
    />
  );
}

/**
 * O cartão das peças, comum à fundação e à estrutura: a tabela por serviço × item, o aço por
 * elemento e bitola, e o que ficou fora — o item sem par e a peça da planilha sem a medida do
 * serviço ou sem a concretagem (RN-555).
 */
function TaxasDasPecas({
  media,
  nomeDaObra,
  titulo,
  descricao,
  semPecas,
  semMedia,
  aco,
}: {
  readonly media: MediaDasPecas;
  readonly nomeDaObra: ReadonlyMap<ProjectId, string>;
  readonly titulo: string;
  readonly descricao: string;
  /** O que dizer quando as obras marcadas não têm nenhuma peça destas. */
  readonly semPecas: string;
  readonly semMedia: { readonly title: string; readonly description: string };
  readonly aco: AcoDasPecasProps;
}) {
  const origem = (average: ApartmentAverage): string =>
    `${quantas(average.projects, 'obra')} · ${quantas(average.unitTypes, 'peça')}`;
  return (
    <Card>
      <CardHeader>
        <CardTitle>{titulo}</CardTitle>
        <CardDescription>{descricao}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {media.pieces === 0 ? (
          <p className="text-sm text-(--color-content-muted)">{semPecas}</p>
        ) : (
          <>
            {media.averages.length === 0 ? (
              <EmptyState
                title={semMedia.title}
                description={semMedia.description}
                icon={<Calculator className="size-8" />}
              />
            ) : (
              <TabelaDeMedias averages={media.averages} origem={origem} />
            )}
            <AcoDasPecas {...aco} lista={media.steel} />
          </>
        )}
        {media.outOfAverage.length > 0 && (
          <section className="flex flex-col gap-1">
            <h3 className="text-sm font-medium">
              Fora da média: {APARTMENT_OUT_LABEL.ITEM_SEM_PAR}
            </h3>
            <ul className="flex flex-col gap-0.5 text-sm text-(--color-content-muted)">
              {media.outOfAverage.map((item) => (
                <li key={`${item.projectId}|${item.serviceName}|${item.description}`}>
                  {nomeDaObra.get(item.projectId) ?? 'Obra'} · {item.serviceName} ·{' '}
                  {item.description}
                  <span className="tabular"> ({quantas(item.unitTypes, 'peça')})</span>
                </li>
              ))}
            </ul>
          </section>
        )}
        {/* A peça da planilha sem a medida do serviço (RN-555). Ausente: o servidor de antes. */}
        {MOTIVOS_DA_PECA_FORA.map((motivo) => {
          const doMotivo = (media.piecesOut ?? []).filter((fora) => fora.reason === motivo);
          if (doMotivo.length === 0) return null;
          return (
            <section key={motivo} className="flex flex-col gap-1">
              <h3 className="text-sm font-medium">
                Fora da média: {FOUNDATION_PIECE_OUT_LABEL[motivo]}
              </h3>
              <ul className="flex flex-col gap-0.5 text-sm text-(--color-content-muted)">
                {doMotivo.map((fora) => (
                  <li key={fora.serviceKey}>
                    {fora.disciplineName} › {fora.serviceName}
                    <span className="tabular">
                      {' '}
                      ({quantas(fora.pieces, 'peça')} de {quantas(fora.projects, 'obra')})
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
      </CardContent>
    </Card>
  );
}

interface AcoDasPecasProps {
  /** "Aço da fundação", "Aço da estrutura" — o nome da seção e o título. */
  readonly rotulo: string;
  /** Os elementos, na ordem em que aparecem. */
  readonly elementos: readonly PieceElement[];
  /** O que dizer sem nenhuma taxa. */
  readonly vazio: string;
  /** Uma linha a mais embaixo do título (o arranque, na estrutura). */
  readonly nota?: string;
}

/**
 * O aço das peças por elemento e bitola (Fase 41, RN-552; Fase 42, RN-580): Σ (taxa × volume) ÷
 * Σ volume, de quantas obras e peças (RN-555).
 */
function AcoDasPecas({
  rotulo,
  elementos,
  vazio,
  nota,
  lista: aco,
}: AcoDasPecasProps & { readonly lista: readonly AcoDoElemento[] }) {
  return (
    <section aria-label={rotulo} className="flex flex-col gap-2">
      <h3 className="text-sm font-medium">{rotulo} (kg/m³)</h3>
      {nota !== undefined && <p className="text-xs text-(--color-content-muted)">{nota}</p>}
      {aco.length === 0 ? (
        <p className="text-sm text-(--color-content-muted)">{vazio}</p>
      ) : (
        elementos.map((elemento) => {
          const doElemento = aco.filter((average) => average.element === elemento);
          if (doElemento.length === 0) return null;
          return (
            <div key={elemento} className="flex flex-col gap-0.5">
              <h4 className="text-xs font-semibold uppercase tracking-[0.06em] text-(--color-content-muted)">
                {APPLIES_TO_LABEL[elemento]}
              </h4>
              <ul
                aria-label={`Aço: ${APPLIES_TO_LABEL[elemento]}`}
                className="flex flex-col gap-0.5 text-sm"
              >
                {doElemento.map((average) => (
                  <li key={average.gauge} className="tabular flex justify-between gap-3">
                    <span>{steelGaugeLabel(average.gauge)}</span>
                    <span>
                      {metros(average.rate, 3)}{' '}
                      <span className="text-(--color-content-muted)">
                        · {quantas(average.projects, 'obra')}
                        {average.pieces !== undefined && ` · ${quantas(average.pieces, 'peça')}`}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })
      )}
    </section>
  );
}

/** "por m³ de estaca", "por kg de aço" — o que a taxa divide, dito por extenso. */
const POR_MEDIDA: Readonly<Record<RateMeasureUnit, string>> = {
  m: 'por m de perfuração',
  'm³': 'por m³ de estaca',
  kg: 'por kg de aço',
  un: 'por estaca',
  estaca: 'por estaca',
};

/**
 * As taxas de um diâmetro, serviço a serviço.
 *
 * O serviço é uma linha de título, e não uma coluna: a coluna repetia o nome em branco em
 * nove de cada dez linhas e roubava a largura que o celular não tem. No celular, a medida e a
 * origem descem para debaixo do item, e a taxa — o número que se veio ver — fica sempre à vista.
 */
function TabelaDoDiametro({ averages }: { readonly averages: readonly RateAverage[] }) {
  const origem = (average: RateAverage): string =>
    `${quantas(average.projects, 'obra')} · ${quantas(average.pieces, 'estaca')}`;
  return (
    <TableWrapper>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Item</TableHead>
            <TableHead className="text-right">Taxa</TableHead>
            <TableHead className="hidden md:table-cell">Medida</TableHead>
            <TableHead className="hidden text-right md:table-cell">Origem</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {averages.flatMap((average, indice) => {
            const linhas = [];
            if (averages[indice - 1]?.serviceKey !== average.serviceKey) {
              linhas.push(
                <TableRow key={`servico|${average.serviceKey}`}>
                  <TableCell
                    colSpan={4}
                    className="bg-(--color-surface-muted) py-1.5 text-xs font-semibold uppercase tracking-[0.06em]"
                  >
                    {average.serviceName}
                  </TableCell>
                </TableRow>,
              );
            }
            linhas.push(
              <TableRow key={`${average.serviceKey}|${average.item.description}`}>
                <TableCell>
                  {average.item.description}
                  {average.item.code !== undefined && (
                    <span className="ml-2 font-mono text-xs text-(--color-content-muted)">
                      {average.item.code}
                    </span>
                  )}
                  <span className="block text-xs text-(--color-content-muted) md:hidden">
                    {POR_MEDIDA[average.measureUnit]} · {origem(average)}
                  </span>
                </TableCell>
                <TableCell className="tabular whitespace-nowrap text-right">
                  {metros(average.rate, 4)}{' '}
                  <span className="text-(--color-content-muted)">
                    {average.item.unit ?? ''}/{average.measureUnit}
                  </span>
                </TableCell>
                <TableCell className="hidden whitespace-nowrap text-sm text-(--color-content-muted) md:table-cell">
                  {POR_MEDIDA[average.measureUnit]}
                </TableCell>
                <TableCell className="tabular hidden whitespace-nowrap text-right text-sm text-(--color-content-muted) md:table-cell">
                  {origem(average)}
                </TableCell>
              </TableRow>,
            );
            return linhas;
          })}
        </TableBody>
      </Table>
    </TableWrapper>
  );
}

const ORDEM_DO_MOTIVO: readonly OutOfAverageReason[] = [
  'SEM_DIAMETRO',
  'SEM_COMPRIMENTO',
  'ITEM_SEM_PAR',
  'SEM_MEDIDA',
];

function ForaDaMedia({
  itens,
  nomeDaObra,
}: {
  readonly itens: readonly OutOfAverage[];
  readonly nomeDaObra: ReadonlyMap<ProjectId, string>;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Fora da média</CardTitle>
        <CardDescription>
          O que as obras marcadas têm e a conta não usou. Corrigir a planilha ou o cadastro da
          referência traz de volta.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {ORDEM_DO_MOTIVO.map((motivo) => {
          const doMotivo = itens.filter((item) => item.reason === motivo);
          if (doMotivo.length === 0) return null;
          return (
            <section key={motivo} className="flex flex-col gap-1">
              <h3 className="text-sm font-medium">
                {primeiraMaiuscula(OUT_OF_AVERAGE_LABEL[motivo])}
              </h3>
              <ul className="flex flex-col gap-0.5 text-sm text-(--color-content-muted)">
                {doMotivo.map((item) => (
                  <li
                    key={`${item.projectId}|${item.pieceName ?? ''}|${item.serviceName ?? ''}|${item.description ?? ''}`}
                  >
                    {nomeDaObra.get(item.projectId) ?? 'Obra'} ·{' '}
                    {[item.pieceName, item.serviceName, item.description]
                      .filter((parte) => parte !== undefined)
                      .join(' · ')}
                    {item.pieceName === undefined && (
                      <span className="tabular"> ({quantas(item.pieces, 'estaca')})</span>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
      </CardContent>
    </Card>
  );
}

function quantas(n: number, singular: string, plural = `${singular}s`): string {
  return `${String(n)} ${n === 1 ? singular : plural}`;
}
